/* ============================================================================
  product-form.js (Cloudflare/429 resilient)
  - Never crashes on HTML responses
  - Prevents double submits
  - If Cloudflare challenge/429 happens, fallback to /cart (or show message)
============================================================================ */

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        if (!this.form) return;

        const idInput = this.form.querySelector('[name=id]');
        if (idInput) idInput.disabled = false;

        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');

        if (this.submitButton && document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.error = false;
        this._inFlight = false;

        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      }

      get variantIdInput() {
        return this.form?.querySelector?.('[name=id]');
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');
        if (!this.errorMessage) return;

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) this.errorMessage.textContent = errorMessage;
      }

      async safeReadText(res) {
        try {
          return await res.text();
        } catch (_e) {
          return '';
        }
      }

      isCloudflareChallengeHtml(text) {
        const t = (text || '').toLowerCase();
        return (
          t.includes('verifying your connection') ||
          t.includes('_cf_chl_opt') ||
          t.includes('cloudflare') ||
          t.includes('cf-injected-html')
        );
      }

      async parseJsonIfPossible(res) {
        if (!res) return { ok: false, data: null, text: '' };

        const ct = (res.headers.get('content-type') || '').toLowerCase();

        // If server says JSON, try json()
        if (ct.includes('application/json') || ct.includes('text/json') || ct.includes('application/javascript')) {
          try {
            const data = await res.json();
            return { ok: res.ok, data, text: '' };
          } catch (_e) {
            // fallthrough to text
          }
        }

        // Otherwise read text (may be Cloudflare HTML)
        const text = await this.safeReadText(res);
        // Try parse json from text if it looks like json
        const trimmed = (text || '').trim();
        if (trimmed && trimmed[0] === '{') {
          try {
            const data = JSON.parse(trimmed);
            return { ok: res.ok, data, text };
          } catch (_e) {}
        }
        return { ok: res.ok, data: null, text };
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        if (!this.submitButton) return;
        if (this._inFlight) return; // hard lock against double submit
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this._inFlight = true;
        this.error = false;

        this.handleErrorMessage(false);

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        const spinner = this.querySelector('.loading__spinner');
        spinner?.classList?.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        // Sections for drawer refresh (Dawn style)
        if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
          try {
            formData.append('sections', this.cart.getSectionsToRender().map((s) => s.id));
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement?.(document.activeElement);
          } catch (_e) {}
        }

        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((res) => this.parseJsonIfPossible(res))
          .then(({ ok, data, text }) => {
            // 429 or HTML challenge
            if (!ok && (text && this.isCloudflareChallengeHtml(text))) {
              this.handleErrorMessage('接続の確認が必要です。ページを開いて確認後、再度お試しください。');
              // Best-effort fallback: go to cart page (often triggers CF verification properly)
              window.location.href = window.routes?.cart_url || '/cart';
              this.error = true;
              return;
            }

            // 429 or non-json error
            if (!ok || !data) {
              this.handleErrorMessage('カート追加に失敗しました。時間をおいて再度お試しください。');
              this.error = true;
              return;
            }

            // Shopify add.js error payload (e.g. sold out)
            if (data.status) {
              try {
                publish?.(PUB_SUB_EVENTS.cartError, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  errors: data.errors || data.description,
                  message: data.message,
                });
              } catch (_e) {}

              this.handleErrorMessage(data.description || '追加できませんでした。');
              this.error = true;
              return;
            }

            // If no drawer/cart component, fallback to cart page
            if (!this.cart || typeof this.cart.renderContents !== 'function') {
              window.location.href = window.routes?.cart_url || '/cart';
              return;
            }

            // Publish update (if available)
            try {
              publish?.(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: data,
              });
            } catch (_e) {}

            // Success: render => cart-drawer.js will open
            this.cart.renderContents(data);
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage('カート追加に失敗しました。');
            this.error = true;
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart?.classList?.contains('is-empty')) this.cart.classList.remove('is-empty');

            if (!this.error) this.submitButton.removeAttribute('aria-disabled');

            spinner?.classList?.add('hidden');
            this._inFlight = false;
          });
      }
    }
  );
}
